import React, { FC, useEffect, useMemo, useState } from 'react'
import {
  AVAILABLE_GRAPHIC,
  ChartProps,
  GRAPHIC_DISPLAY_NAME,
  TGraphicSettings,
  INTERVAL,
  TInterval
} from '../../../models/analytics'
import { SOCIAL_NETWORK } from '../../../models/post'
import { TIntervalValue } from '../../../pages/Analytics'

import RadioInput from '../../UI/Inputs/Radio/Simple'
import Form from '../../Form'
import SelectInput from '../../UI/Inputs/SelectInput'
import QuestionIcon from '../../UI/Icons/Question'

import './Graphic.scss'

type GraphicProps = {
  settings: TGraphicSettings
  name: AVAILABLE_GRAPHIC
  chart: FC<ChartProps>
  social: SOCIAL_NETWORK
  data: TIntervalValue
  setFollowerInterval?: React.Dispatch<React.SetStateAction<TInterval>>
  tooltip?: string
}

enum AGGREGATED_CHART_TYPE {
  ALL = 'all',
  ORGANIC = 'organic',
  PAID = 'paid'
}

const transformPeriodName = (period: string) => {
  const displayPeriod = period.replaceAll('-', ' ').split('')
  displayPeriod.splice(0, 1, displayPeriod[0].toLocaleUpperCase())
  return displayPeriod.join('')
}

const followerChartConfig = [AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS,AVAILABLE_GRAPHIC.TOTAL_NEW_FOLLOWERS,AVAILABLE_GRAPHIC.NEW_FOLLOWERS]

const Graphic: FC<GraphicProps> = ({
  name,
  settings,
  chart: Chart,
  social,
  data,
  setFollowerInterval,
  tooltip
}) => {
  const [isOrganic, setIsOrganic] = useState<AGGREGATED_CHART_TYPE>(AGGREGATED_CHART_TYPE.ALL)
  const [isCumulative, setIsCumulative] = useState<boolean>(false)
  const [interval, setInterval] = useState<TInterval>(TInterval.daily)
  const intervalList = [
    { label: transformPeriodName(TInterval.daily), value: TInterval.daily },
    { label: transformPeriodName(TInterval.weekly), value: TInterval.weekly },
    { label: transformPeriodName(TInterval.monthly), value: TInterval.monthly },
    { label: transformPeriodName(TInterval.quarterly), value: TInterval.quarterly }
  ]
  const cumulativeList = [
    { label: 'Non-cumulative', value: false },
    { label: 'Cumulative', value: true }
  ]

  useEffect(() => {
    if (setFollowerInterval && interval &&
       (name === AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS || name === AVAILABLE_GRAPHIC.TOTAL_NEW_FOLLOWERS ||
        name === AVAILABLE_GRAPHIC.NEW_FOLLOWERS)) {
      setFollowerInterval(interval)
    }
  }, [interval])

  const average = useMemo(() => {
   
    // if (isCumulative) {
    //   if (settings.organic) {
    //     if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
    //       const {
    //         cumulative: { organic }
    //       } = data[interval][name]!
    //       return organic?.average?organic?.average:0
    //     } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
    //       const {
    //         cumulative: { paid }
    //       } = data[interval][name]!
    //       return paid?.average?paid?.average:0
    //     } else {
    //       const {
    //         cumulative: { all }
    //       } = data[interval][name]!
    //       return all?.average?all?.average:0
    //     }
    //   } else {
    //     const { cumulative } = data[interval][name]!
    //     return cumulative.average?cumulative.average:0
    //   }
    // } else {
      if (settings.organic) {
        if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
          const {
            non_cumulative: { organic }
          } = data[interval][name]!
          return organic?.average?organic?.average:0
        } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
          const {
            non_cumulative: { paid }
          } = data[interval][name]!
          return paid?.average?paid?.average:0
        } else {
         
          const {
            non_cumulative: { all }
          } = data[interval][name]!
          return all?.average?all?.average:0
         
        }
      } else {
        const { non_cumulative: nonCumulative } = data[interval][name]!
        return nonCumulative?.average?nonCumulative?.average:0
      }
    // }
  }, [name, isCumulative, interval, settings, isOrganic])

  const chartData = useMemo(() => {
    switch (name) {
      case AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS: {
        if (isCumulative) {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                cumulative: { all }
              } = data[interval][name]!

              return all
            }
          } else {
            const { cumulative } = data[interval][name]!
            return cumulative
          }
        } else {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                non_cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                non_cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                non_cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { non_cumulative: nonCumulative } = data[interval][name]!
            return nonCumulative
          }
        }
      }

      case AVAILABLE_GRAPHIC.NEW_FOLLOWERS: {
        if (isCumulative) {
          const { cumulative } = data[interval][name]!
          return cumulative
        } else {
          const { non_cumulative: nonCumulative } = data[interval][name]!
          return nonCumulative
        }
      }

      case AVAILABLE_GRAPHIC.TOTAL_NEW_FOLLOWERS: {
        if (isCumulative) {
          const { cumulative } = data[interval][name]!
          return cumulative
        } else {
          const { non_cumulative: nonCumulative } = data[interval][name]!
          return nonCumulative
        }
      }

      case AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS: {
        if (isCumulative) {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { cumulative } = data[interval][name]!
            return cumulative
          }
        } else {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                non_cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                non_cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                non_cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { non_cumulative } = data[interval][name]!
            return non_cumulative
          }
        }
      }

      case AVAILABLE_GRAPHIC.TOTAL_REACH: {
        if (isCumulative) {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { cumulative } = data[interval][name]!
            return cumulative
          }
        } else {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                non_cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                non_cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                non_cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { non_cumulative } = data[interval][name]!
            return non_cumulative
          }
        }
      }

      case AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT: {
        if (isCumulative) {
          const { cumulative } = data[interval][name]!
          return cumulative
        } else {
          const { non_cumulative: nonCumulative } = data[interval][name]!
          return nonCumulative
        }
      }

      case AVAILABLE_GRAPHIC.PROFILE_IMPRESSIONS: {
        if (isCumulative) {
          const { cumulative } = data[interval][name]!
          return cumulative
        } else {
          const { non_cumulative: nonCumulative } = data[interval][name]!
          return nonCumulative
        }
      }

      case AVAILABLE_GRAPHIC.STORIES_REACH:
      case AVAILABLE_GRAPHIC.STORIES_ENGAGEMENT: {
        if (isCumulative) {
          const { cumulative } = data[interval][name]!
          return cumulative
        } else {
          const { non_cumulative: nonCumulative } = data[interval][name]!
          return nonCumulative
        }
      }

      case AVAILABLE_GRAPHIC.ENGAGEMENT_RATE: {
        if (isCumulative) {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { cumulative } = data[interval][name]!
            return cumulative
          }
        } else {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                non_cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                non_cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                non_cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { non_cumulative } = data[interval][name]!
            return non_cumulative
          }
        }
      }

      case AVAILABLE_GRAPHIC.RETWEET: {
        if (isCumulative) {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { cumulative } = data[interval][name]!
            return cumulative
          }
        } else {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                non_cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                non_cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                non_cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { non_cumulative } = data[interval][name]!
            return non_cumulative
          }
        }
      }

      case AVAILABLE_GRAPHIC.TWEET_LIKES: {
        if (isCumulative) {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { cumulative } = data[interval][name]!
            return cumulative
          }
        } else {
          if (settings.organic) {
            if (isOrganic === AGGREGATED_CHART_TYPE.ORGANIC) {
              const {
                non_cumulative: { organic }
              } = data[interval][name]!
              return organic
            } else if (isOrganic === AGGREGATED_CHART_TYPE.PAID) {
              const {
                non_cumulative: { paid }
              } = data[interval][name]!
              return paid
            } else {
              const {
                non_cumulative: { all }
              } = data[interval][name]!
              return all
            }
          } else {
            const { non_cumulative } = data[interval][name]!
            return non_cumulative
          }
        }
      }

      case AVAILABLE_GRAPHIC.TOTAL_PAGE_VIEWS: {
        if (isCumulative) {
          const { cumulative } = data[interval][name]!
          return cumulative
        } else {
          const { non_cumulative } = data[interval][name]!
          return non_cumulative
        }
      }
    }
  }, [isOrganic, isCumulative, settings, data, name, interval])

  const setUpForm = {
    initialValues: {
      interval: '',
      cumulative: ''
    },
    onSubmit: async () => {}
  }

  return (
    <div className='graphic-wrapper'>
      <div className={followerChartConfig.includes(name) && social !== SOCIAL_NETWORK.LINKEDIN_SOCIAL?'graphic-wrapper__header graphic-wrapper__header-end':'graphic-wrapper__header'}>
        {!followerChartConfig.includes(name)?<div className='graphic-wrapper__header__name'>
          {GRAPHIC_DISPLAY_NAME[name] ? GRAPHIC_DISPLAY_NAME[name] : name.replaceAll('-', ' ')}
          {tooltip?<div className='ml-5 custom-tooltip'><QuestionIcon />
          <div className='custom-tooltip-block'>{tooltip}</div>
          </div>:null}
        </div>: null}
        {settings.organic && (
          <div className='graphic-wrapper__header__toggle-organic'>
            <RadioInput
              checked={isOrganic === AGGREGATED_CHART_TYPE.ALL}
              handleChange={() => setIsOrganic(AGGREGATED_CHART_TYPE.ALL)}
              label='All'
              value={true}
              name='isOrganic'
            />
            <RadioInput
              checked={isOrganic === AGGREGATED_CHART_TYPE.ORGANIC}
              handleChange={() => setIsOrganic(AGGREGATED_CHART_TYPE.ORGANIC)}
              label='Organic'
              value={true}
              name='isOrganic'
            />
            <RadioInput
              checked={isOrganic === AGGREGATED_CHART_TYPE.PAID}
              handleChange={() => setIsOrganic(AGGREGATED_CHART_TYPE.PAID)}
              label='Paid'
              value={false}
              name='isOrganic'
            />
          </div>
        )}

        <Form {...setUpForm}>
          <div className='graphic-wrapper__dropdown-wrapper'>
            {setInterval && (
              <div className='graphic-wrapper__dropdown-wrapper__dropdown'>
                <SelectInput
                  defaultValue={intervalList[0]}
                  name='interval'
                  placeholder=''
                  options={intervalList}
                  setChanges={setInterval}
                />
              </div>
            )}
            {settings.cumulative && (
              <div className='graphic-wrapper__dropdown-wrapper__dropdown-cumulative'>
                <SelectInput
                  defaultValue={cumulativeList[0]}
                  name='cumulative'
                  placeholder=''
                  options={cumulativeList}
                  setChanges={setIsCumulative}
                />
              </div>
            )}
            <div className='graphic-average-wrapper'>
              <div>
                <div className='graphic-average-text'>Average: {Math.round(average?average:0)}</div>
              </div>
            </div>
          </div>
        </Form>
        {/* {settings.cumulative && (
          <div className='graphic-wrapper__header__toggle-cumulative'>
            <button
              className={`${!isCumulative && 'active'}`}
              onClick={() => setIsCumulative(false)}
            >
              Non-cumulative
            </button>
            <button className={`${isCumulative && 'active'}`} onClick={() => setIsCumulative(true)}>
              Cumulative
            </button>
          </div>
        )} */}
      </div>
      <div className='graphic-wrapper__mokup-graphic'>
        <Chart name={name} social={social} chartData={chartData} />
      </div>
    </div>
  )
}

export default Graphic
